/**
 * coverDefault.js — the host's DEFAULT cover tile for `cover_type:"preset"`.
 *
 * A widget's catalog COVER (the ~240×170 tile thumbnail) is EITHER:
 *   - `cover_type:"html"`   → the bundle ships a hand-authored `cover.html`, OR
 *   - `cover_type:"preset"` (DEFAULT) → the HOST renders the tile from one of 5
 *     built-in presets. No `cover.html` needed.
 *
 * Each preset reads the widget's data from `window.__WIDGET_DATA__` (the same
 * example_data / instance feed the studio + catalog already inject before the
 * tile renders) and frames a DIFFERENT facet of it — they're meant to look
 * distinct so a catalog of widgets feels varied:
 *   count   — big tally of entries
 *   latest  — the most recent entry's image, full-bleed, title overlaid
 *   gallery — a mini grid of recent entry images
 *   list    — title + a peek at the most recent entry names
 *   stat    — title + a compact two-stat strip
 *   card    — a DESIGNED plate (3 fanned mini-cards + eyebrow/title/chip foot),
 *             configured by `widget.json.cover_config` text knobs (derived from
 *             the hand-authored Art Studio / Personal Studio covers); with no
 *             config it degrades to a data-driven fan of the newest images
 *
 * The returned string is a complete, self-contained HTML document for a
 * sandboxed `allow-scripts` iframe: it inlines its styles and a tiny render
 * script that reads `window.__WIDGET_DATA__` / `window.__USER_DATA__` at runtime
 * (the cover.html contract). Theme-aware via `__USER_DATA__.appearance`. The
 * iPhone app implements the same preset ids natively.
 *
 * No icon is drawn in the cover — the icon is its own studio face.
 */

export const TOKENS_CSS = `
:root{color-scheme:light dark;
--bg:#F2F2F7;--surface:#FFFFFF;--surface-2:#F7F7FA;--surface-3:#ECECF1;
--text:#1C1C1E;--text-2:#4A4A4F;--text-3:#8E8E93;
--line:rgba(0,0,0,0.10);--line-soft:rgba(0,0,0,0.05);
--accent:#9A8E7A;--accent-ink:#FFFFFF;--accent-soft:rgba(154,142,122,0.14);--accent-line:rgba(154,142,122,0.40);
--r-sm:10px;--r:16px;--r-lg:24px;--r-pill:999px;
--shadow-1:0 1px 3px rgba(0,0,0,0.06),0 1px 2px rgba(0,0,0,0.04);
--t-xs:11px;--t-sm:13px;--t-md:15px;--t-lg:17px;--t-xl:20px;--t-2xl:26px;--t-3xl:34px;--t-display:44px;
--sp-1:4px;--sp-2:8px;--sp-3:12px;--sp-4:16px;
--font-body:-apple-system,BlinkMacSystemFont,"SF Pro Text",system-ui,sans-serif;--font-display:var(--font-body);}
[data-theme="dark"]{--bg:#000000;--surface:#1C1C1E;--surface-2:#2C2C2E;--surface-3:#3A3A3C;
--text:#F5F5F7;--text-2:#C9C9CE;--text-3:#8E8E93;--line:rgba(255,255,255,0.12);--line-soft:rgba(255,255,255,0.06);
--accent-soft:rgba(154,142,122,0.22);--accent-line:rgba(154,142,122,0.50);}
@media(prefers-color-scheme:dark){:root:not([data-theme]){--bg:#000000;--surface:#1C1C1E;--surface-2:#2C2C2E;--surface-3:#3A3A3C;
--text:#F5F5F7;--text-2:#C9C9CE;--text-3:#8E8E93;--line:rgba(255,255,255,0.12);--line-soft:rgba(255,255,255,0.06);
--accent-soft:rgba(154,142,122,0.22);--accent-line:rgba(154,142,122,0.50);}}
*{box-sizing:border-box;margin:0;padding:0;}
html,body{height:100%;background:var(--bg);color:var(--text);font-family:var(--font-body);-webkit-font-smoothing:antialiased;overflow:hidden;}
::-webkit-scrollbar{display:none;}
`;

const COVER_CSS = `
/* #cz is the mount node: it must carry a real height so the percentage chain
   (html→body→#cz→.cz-pad) resolves — .cz-pad's height:100% is declared after
   .cz's 100vh (same specificity, later wins) and against an auto-height parent
   it collapses to CONTENT height, which left bottom-pinned children (the stat
   filmstrip) floating above the tile bottom. */
#cz{height:100%;}
.cz{height:100vh;width:100vw;overflow:hidden;position:relative;}
.cz-eyebrow{font-size:var(--t-xs);letter-spacing:0.08em;text-transform:uppercase;color:var(--text-3);font-weight:700;}
.cz-title{font-family:var(--font-display);font-weight:800;color:var(--text);line-height:1.08;letter-spacing:-0.01em;
  display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;}
.cz-pad{padding:var(--sp-4);display:flex;flex-direction:column;height:100%;}
/* count */
.cz-count{align-items:flex-start;justify-content:space-between;background:radial-gradient(120% 120% at 100% 0%,var(--accent-soft),transparent 60%);}
.cz-count.cz-has-mini{flex-direction:row;align-items:stretch;gap:var(--sp-3);}
.cz-count .cz-col{display:flex;flex-direction:column;justify-content:space-between;min-width:0;}
.cz-count .cz-mini{display:grid;grid-template-columns:1fr 1fr;grid-template-rows:1fr 1fr;gap:4px;
  width:96px;flex:0 0 auto;align-self:center;aspect-ratio:1;}
/* 1-2 images: the peek owns exactly the RIGHT HALF of the cover (left edge at
   the middle), full content height — not a squat square column. */
.cz-count.cz-has-mini .cz-col{flex:0 0 calc(50% - var(--sp-3));}
.cz-count .cz-mini.cz-n1,.cz-count .cz-mini.cz-n2{
  flex:1 1 auto;width:auto;aspect-ratio:auto;align-self:stretch;}
.cz-count .cz-mini.cz-n1{grid-template-columns:1fr;grid-template-rows:1fr;}
.cz-count .cz-mini.cz-n2{grid-template-columns:1fr;grid-template-rows:1fr 1fr;}
.cz-count .cz-mini.cz-n3 .cz-c:first-child{grid-column:1/3;}
.cz-count .cz-mini .cz-c{border-radius:9px;background-size:cover;background-position:center;background-color:var(--surface-3);}
.cz-count .cz-num{font-family:var(--font-display);font-weight:800;font-size:64px;line-height:0.9;color:var(--accent);}
.cz-count.cz-has-mini .cz-num{font-size:46px;}
.cz-count .cz-lbl{font-size:var(--t-sm);color:var(--text-2);text-transform:uppercase;letter-spacing:0.06em;}
.cz-count .cz-title{font-size:var(--t-lg);}
/* latest (full-bleed image) */
.cz-latest .cz-bg{position:absolute;inset:0;background-size:cover;background-position:center;}
.cz-latest .cz-scrim{position:absolute;inset:0;background:linear-gradient(180deg,rgba(0,0,0,0) 35%,rgba(0,0,0,0.72) 100%);}
.cz-latest .cz-fg{position:absolute;left:0;right:0;bottom:0;padding:var(--sp-4);color:#fff;}
.cz-latest .cz-title{color:#fff;font-size:var(--t-xl);}
.cz-latest .cz-sub{font-size:var(--t-sm);color:rgba(255,255,255,0.85);margin-top:2px;
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.cz-latest.cz-noimg{background:linear-gradient(150deg,var(--accent),color-mix(in srgb,var(--accent) 60%,#000));}
.cz-latest.cz-noimg .cz-scrim{display:none;}
/* gallery grid — adapts to the image count: 1 full-bleed · 2 halves ·
   3 = tall left + two right · 4+ = 2x2. No half-empty grids. */
.cz-gallery .cz-grid{position:absolute;inset:0;display:grid;gap:2px;
  grid-template-columns:1fr 1fr;grid-template-rows:1fr 1fr;}
.cz-gallery .cz-grid.cz-n1{grid-template-columns:1fr;grid-template-rows:1fr;}
.cz-gallery .cz-grid.cz-n2{grid-template-columns:1fr 1fr;grid-template-rows:1fr;}
.cz-gallery .cz-grid.cz-n3 .cz-cell:first-child{grid-row:1/3;}
.cz-gallery .cz-cell{background-size:cover;background-position:center;background-color:var(--surface-3);}
.cz-gallery .cz-strip{position:absolute;left:0;right:0;bottom:0;padding:10px var(--sp-4);
  background:linear-gradient(180deg,rgba(0,0,0,0),rgba(0,0,0,0.7));color:#fff;}
.cz-gallery .cz-title{color:#fff;font-size:var(--t-lg);-webkit-line-clamp:1;}
/* list */
.cz-list{background:var(--surface);}
.cz-list .cz-title{font-size:var(--t-lg);margin:2px 0 6px;}
.cz-list .cz-row{display:flex;align-items:center;gap:var(--sp-2);font-size:var(--t-sm);color:var(--text-2);
  padding:4px 0;border-top:1px solid var(--line-soft);white-space:nowrap;overflow:hidden;}
.cz-list .cz-row:first-of-type{border-top:0;}
.cz-list .cz-thumb{width:24px;height:24px;border-radius:7px;flex:0 0 auto;
  background-size:cover;background-position:center;background-color:var(--surface-3);}
.cz-list .cz-thumb.cz-mono{display:inline-flex;align-items:center;justify-content:center;
  font-size:11px;font-weight:700;color:var(--accent);background:var(--accent-soft);}
.cz-list .cz-name{overflow:hidden;text-overflow:ellipsis;}
/* stat */
.cz-stat{background:var(--surface);justify-content:space-between;}
.cz-stat .cz-title{font-size:var(--t-lg);}
.cz-stat .cz-stats{display:flex;gap:var(--sp-4);}
.cz-stat .cz-s{display:flex;flex-direction:column;}
.cz-stat .cz-v{font-family:var(--font-display);font-weight:800;font-size:var(--t-2xl);color:var(--accent);line-height:1;}
.cz-stat .cz-k{font-size:var(--t-xs);text-transform:uppercase;letter-spacing:0.06em;color:var(--text-3);margin-top:3px;}
.cz-stat.cz-has-strip{padding-bottom:calc(46px + var(--sp-2));}
.cz-stat .cz-filmstrip{display:grid;gap:2px;height:46px;
  position:absolute;left:0;right:0;bottom:0;}
.cz-stat .cz-sc{background-size:cover;background-position:center;background-color:var(--surface-3);}
/* card — the designed plate, a 1:1 port of the hand-authored Art Studio /
   Personal Studio cover.html skeleton. Pixel values are that file's vw sizes
   resolved at the 240-wide design canvas (e.g. 30vw→72px, 5vw→12px) and the
   chip/ink colors are its exact rgba values — switching those two widgets
   from cover.html to this preset must be visually indistinguishable. */
.cz-card{--cz-chip:rgba(0,0,0,.06);--cz-lab:rgba(0,0,0,.35);
  padding:11px 12px;display:flex;flex-direction:column;justify-content:space-between;gap:6px;}
[data-theme="dark"] .cz-card{--cz-chip:rgba(255,255,255,.12);--cz-lab:rgba(255,255,255,.4);}
@media(prefers-color-scheme:dark){:root:not([data-theme]) .cz-card{--cz-chip:rgba(255,255,255,.12);--cz-lab:rgba(255,255,255,.4);}}
.cz-card .cz-fan{display:flex;justify-content:center;align-items:flex-start;padding-top:4px;flex:none;}
.cz-card .cz-fc{width:72px;background:var(--surface);border-radius:12px;padding:4px 4px 3px;
  box-shadow:0 3px 10px rgba(0,0,0,.08),inset 0 0 0 .5px var(--line);
  animation:czrise .55s cubic-bezier(.22,.61,.36,1) both;}
.cz-card .cz-fc .cz-art{height:55px;border-radius:8px;overflow:hidden;display:flex;
  align-items:center;justify-content:center;background-size:cover;background-position:center;}
.cz-card .cz-fc .cz-art.cz-contain{background-size:contain;background-repeat:no-repeat;}
.cz-card .cz-fc .cz-art.cz-emoji{background:var(--accent-soft);font-size:26px;line-height:1;}
.cz-card .cz-fc .cz-art.cz-mono{background:var(--accent-soft);font-size:20px;font-weight:800;color:var(--accent);}
.cz-card .cz-fc .cz-flab{display:block;text-align:center;font-size:7px;font-weight:700;letter-spacing:.09em;
  text-transform:uppercase;color:var(--cz-lab);padding:4px 0 1px;white-space:nowrap;overflow:hidden;}
.cz-card .cz-fc.cz-a{transform:rotate(-6deg) translateY(6px);animation-delay:.04s;}
.cz-card .cz-fc.cz-b{z-index:2;margin:0 -7px;transform:translateY(-1px) scale(1.07);
  box-shadow:0 7px 20px rgba(0,0,0,.14),inset 0 0 0 .5px var(--line);animation-delay:.12s;}
.cz-card .cz-fc.cz-c{transform:rotate(6deg) translateY(6px);animation-delay:.2s;}
.cz-card .cz-foot{display:flex;align-items:flex-end;justify-content:space-between;gap:10px;min-width:0;
  animation:czrise .55s cubic-bezier(.22,.61,.36,1) .26s both;}
.cz-card .cz-eyebrow{font-size:9px;letter-spacing:.14em;color:var(--cz-lab);}
.cz-card .cz-title{font-size:13px;line-height:1.16;letter-spacing:-.02em;margin-top:3px;}
.cz-card .cz-chip{display:inline-flex;align-items:center;padding:4px 10px;border-radius:var(--r-pill);
  flex:none;margin-bottom:1px;background:var(--cz-chip);color:var(--text);
  font-size:9px;font-weight:700;letter-spacing:.02em;white-space:nowrap;}
@keyframes czrise{from{opacity:0;transform:translateY(8px);}to{opacity:1;}}
@media (prefers-reduced-motion:reduce){.cz-card *{animation:none!important;}}
`;

// The 5 cover presets in display order. id → studio switch value + the
// widget.json.cover_preset value; label → the studio switch option text.
export const COVER_PRESETS = [
  { id: 'card', label: 'Card' },
  { id: 'count', label: 'Count' },
  { id: 'latest', label: 'Latest' },
  { id: 'gallery', label: 'Gallery' },
  { id: 'list', label: 'List' },
  { id: 'stat', label: 'Stat' },
];
const _COVER_IDS = COVER_PRESETS.map((p) => p.id);

// The deterministic no-preference pick stays on the 5 DATA-driven presets:
// `card` is a designed plate that shines with authored cover_config text, so
// it is chosen, never randomly assigned to legacy widgets.
const _PICK_IDS = _COVER_IDS.filter((id) => id !== 'card');

/** Normalize an arbitrary cover preset value to a known id (fallback: count). */
export function normalizeCoverPreset(p) {
  return _COVER_IDS.includes(p) ? p : 'count';
}

/** Deterministically pick a cover preset from a seed (e.g. widget id), so
 *  widgets with no explicit cover_preset still render a VARIED cover across
 *  the catalog (the zero-write "migration"). */
export function pickCoverPreset(seed) {
  const s = String(seed || '');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return _PICK_IDS[h % _PICK_IDS.length];
}

/** The card preset's text/visual config from a manifest (`cover_config`). */
export function coverConfigOf(manifest) {
  const c = manifest && manifest.cover_config;
  return c && typeof c === 'object' && !Array.isArray(c) ? c : null;
}

/** The cover form for a manifest: "html" only when explicitly set, else the
 *  default "preset" (this is the zero-write migration — every legacy widget
 *  without cover_type renders a preset cover). */
export function coverTypeOf(manifest) {
  return manifest && manifest.cover_type === 'html' ? 'html' : 'preset';
}

/** The preset id for a manifest: its cover_preset if set, else a deterministic
 *  pick from `seed` (the widget id) so the catalog looks varied. */
export function coverPresetFor(manifest, seed) {
  const p = manifest && manifest.cover_preset;
  return p ? normalizeCoverPreset(p) : pickCoverPreset(seed);
}

// The runtime render script (one function, switches on the baked PRESET). Reads
// window.__WIDGET_DATA__ + __USER_DATA__; degrades gracefully when data is thin.
const RENDER_JS = `
(function(){
  var D = window.__WIDGET_DATA__ || {}; var U = window.__USER_DATA__ || {};
  var ap = U.appearance; if (ap === 'light' || ap === 'dark') document.documentElement.setAttribute('data-theme', ap);
  var title = D.title || D.name || U.instance_title || U.title || 'Widget';
  var entries = Array.isArray(D.entries) ? D.entries : (Array.isArray(D.items) ? D.items : []);
  var count = entries.length;
  function esc(s){ return String(s==null?'':s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;'); }
  function img(e){ var p=(e&&e.payload)||e||{}; var v=Array.isArray(p.images)?p.images[0]:(p.image_url||p.image||p.thumbnail); return v||''; }
  function nm(e){ var p=(e&&e.payload)||e||{}; return p.name||p.title||p.label||''; }
  function cssUrl(u){ return "url('" + String(u).replace(/'/g,"%27").replace(/&/g,'&amp;') + "')"; }
  var rawImgs = entries.map(img).filter(Boolean).slice(0, 8);
  var CFG = (typeof COVER_CONFIG === 'object' && COVER_CONFIG) ? COVER_CONFIG : {};
  var root = document.getElementById('cz'); if(!root) return;
  // Preload gate: a stored ref whose object is GONE (e.g. its source session
  // was deleted) must not paint a dead grey cell — keep only images that load,
  // then let the adaptive layouts fit however many survive. data: URIs pass
  // through untested (they cannot 404).
  function preload(u){
    if (String(u).indexOf('data:') === 0) return Promise.resolve(u);
    return new Promise(function(res){
      var im = new Image();
      var done = false;
      function fin(ok){ if (done) return; done = true; res(ok ? u : null); }
      im.onload = function(){ fin(true); };
      im.onerror = function(){ fin(false); };
      setTimeout(function(){ fin(false); }, 5000);
      im.src = u;
    });
  }
  Promise.all(rawImgs.map(preload)).then(function(loaded){
    render(loaded.filter(Boolean));
  });
  function render(imgs){
  var html='';
  if (PRESET === 'card') {
    // The designed plate. Authored cover_config drives every string; with no
    // config it degrades to a data fan (newest images + entry names) so an
    // unconfigured widget still renders a complete tile, never a blank.
    var dark = (ap === 'dark') || (ap !== 'light' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches);
    var cards = Array.isArray(CFG.cards) ? CFG.cards.slice(0,3) : [];
    if (!cards.length) {
      cards = entries.slice(0,3).map(function(e){ return { label: nm(e), image: img(e) }; });
    }
    while (cards.length < 3) cards.push({});
    var pos = ['cz-a','cz-b','cz-c'];
    var fan = '';
    for (var ci=0; ci<3; ci++) {
      var c = cards[ci] || {};
      var u = (dark && c.image_dark) ? c.image_dark : c.image;
      var art;
      if (u) {
        art = '<div class="cz-art'+(c.fit==='contain'?' cz-contain':'')+'" style="background-image:'+cssUrl(u)+'"></div>';
      } else if (c.emoji) {
        art = '<div class="cz-art cz-emoji">'+esc(c.emoji)+'</div>';
      } else {
        var mch = (c.label||title).charAt(0).toUpperCase() || '•';
        art = '<div class="cz-art cz-mono">'+esc(mch)+'</div>';
      }
      var lab = c.label ? '<span class="cz-flab">'+esc(c.label)+'</span>' : '<span class="cz-flab">&nbsp;</span>';
      fan += '<div class="cz-fc '+pos[ci]+'">'+art+lab+'</div>';
    }
    var cardTitle = CFG.title || title;
    var titleHtml = esc(cardTitle).replace(/\\n/g,'<br>');
    var chip = CFG.chip ? '<div class="cz-chip">'+esc(CFG.chip)+'</div>' : '';
    html = '<div class="cz cz-card"><div class="cz-fan">'+fan+'</div>'
      + '<div class="cz-foot"><div style="min-width:0">'
      + '<div class="cz-eyebrow">'+esc(CFG.eyebrow||title)+'</div>'
      + '<div class="cz-title">'+titleHtml+'</div></div>'+chip+'</div></div>';
  } else if (PRESET === 'count') {
    // Data-gated right-side 2x2 image peek; imageless keeps the pure-count look.
    var mini='';
    if (imgs.length) {
      var mn=Math.min(imgs.length,4);
      var mc=''; for (var m=0;m<mn;m++){ mc += '<div class="cz-c" style="background-image:'+cssUrl(imgs[m])+'"></div>'; }
      mini = '<div class="cz-mini cz-n'+mn+'">'+mc+'</div>';
    }
    html = '<div class="cz cz-pad cz-count'+(imgs.length?' cz-has-mini':'')+'">'
      + '<div class="cz-col"><div class="cz-eyebrow">'+esc(D.eyebrow||'Folder')+'</div>'
      + '<div><div class="cz-num">'+(count||0)+'</div>'
      + '<div class="cz-lbl">'+esc(count===1?'item':'items')+'</div></div>'
      + '<div class="cz-title">'+esc(title)+'</div></div>'
      + mini + '</div>';
  } else if (PRESET === 'latest') {
    var hero = imgs[0]; var latestName = nm(entries[0]);
    html = '<div class="cz cz-latest'+(hero?'':' cz-noimg')+'">'
      + (hero?'<div class="cz-bg" style="background-image:'+cssUrl(hero)+'"></div><div class="cz-scrim"></div>':'')
      + '<div class="cz-fg"><div class="cz-title">'+esc(title)+'</div>'
      + (latestName?'<div class="cz-sub">'+esc(latestName)+'</div>':'')+'</div></div>';
  } else if (PRESET === 'gallery') {
    var gn=Math.max(1,Math.min(imgs.length,4));
    var cells=''; for (var i=0;i<gn;i++){ var g=imgs[i]; cells += '<div class="cz-cell"'+(g?' style="background-image:'+cssUrl(g)+'"':'')+'></div>'; }
    html = '<div class="cz cz-gallery"><div class="cz-grid cz-n'+gn+'">'+cells+'</div>'
      + '<div class="cz-strip"><div class="cz-title">'+esc(title)+'</div></div></div>';
  } else if (PRESET === 'list') {
    // Thumb rows: each row leads with the item's image (monogram fallback).
    var rows=''; var top=entries.slice(0,3);
    function thumbOf(e, j){
      var u=img(e);
      if (u) return '<span class="cz-thumb" style="background-image:'+cssUrl(u)+'"></span>';
      var ch=(nm(e)||'').charAt(0).toUpperCase()||String(j+1);
      return '<span class="cz-thumb cz-mono">'+esc(ch)+'</span>';
    }
    if (top.length){ for (var j=0;j<top.length;j++){ rows += '<div class="cz-row">'+thumbOf(top[j], j)+'<span class="cz-name">'+esc(nm(top[j])||('Item '+(j+1)))+'</span></div>'; } }
    else { rows = '<div class="cz-row"><span class="cz-thumb cz-mono">•</span><span class="cz-name">No items yet</span></div>'; }
    html = '<div class="cz cz-pad cz-list"><div class="cz-eyebrow">'+(count?count+(count===1?' item':' items'):esc(D.eyebrow||'Folder'))+'</div>'
      + '<div class="cz-title">'+esc(title)+'</div>'+rows+'</div>';
  } else { /* stat */
    var second = (typeof D.stat_value!=='undefined') ? D.stat_value : (imgs.length||0);
    var secondLabel = D.stat_label || 'with photo';
    // The bottom band carries a horizontal image filmstrip (data-gated).
    var strip='';
    if (imgs.length) {
      var sn=Math.min(imgs.length,4);
      var fc=''; for (var f2=0;f2<sn;f2++){ fc += '<div class="cz-sc" style="background-image:'+cssUrl(imgs[f2])+'"></div>'; }
      strip = '<div class="cz-filmstrip" style="grid-template-columns:repeat('+sn+',1fr)">'+fc+'</div>';
    }
    html = '<div class="cz cz-pad cz-stat'+(strip?' cz-has-strip':'')+'"><div><div class="cz-eyebrow">'+esc(D.eyebrow||'Overview')+'</div>'
      + '<div class="cz-title">'+esc(title)+'</div></div>'
      + '<div><div class="cz-stats"><div class="cz-s"><div class="cz-v">'+(count||0)+'</div><div class="cz-k">items</div></div>'
      + '<div class="cz-s"><div class="cz-v">'+esc(second)+'</div><div class="cz-k">'+esc(secondLabel)+'</div></div></div>'
      + strip + '</div></div>';
  }
  root.innerHTML = html;
  }
})();
`;

/**
 * Build the default cover HTML for a `cover_type:"preset"` widget.
 * @param {string} [preset]  one of COVER_PRESETS ids (default/fallback: count)
 * @param {object} [coverConfig]  widget.json.cover_config (card preset text/visual knobs)
 * @returns {string} a complete standalone HTML document (reads __WIDGET_DATA__)
 */
export function buildDefaultCoverHtml(preset, coverConfig) {
  const presetId = normalizeCoverPreset(preset);
  const cfg = coverConfig && typeof coverConfig === 'object' ? coverConfig : null;
  return `<!doctype html><html><head><meta charset="utf-8">`
    + `<meta name="viewport" content="width=device-width,initial-scale=1">`
    + `<style>${TOKENS_CSS}${COVER_CSS}</style></head><body><div id="cz"></div>`
    + `<script>var PRESET=${JSON.stringify(presetId)};var COVER_CONFIG=${JSON.stringify(cfg)};</script>`
    + `<script>${RENDER_JS}</script></body></html>`;
}
